import { Button, Paper, Stack, styled, Table, TableBody, TableContainer, TableHead, TableRow } from '@ethings/ui-toolkit';
import { SxProps, unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { FC, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveText } from '../responsive-text';

interface Props {
  'data-testid'?: string;
  actionComponent?: JSX.Element;
  hasShowAll?: boolean;
  headComponent: JSX.Element;
  hintComponent: JSX.Element;
  listComponent: JSX.Element[];
  noPadding?: boolean;
  noTitle?: boolean;
  tableContainerSx?: SxProps;
  tablesx?: SxProps;
  titleComponent: JSX.Element;
}

interface StyledTableContainerProps {
  tableContainerSx?: SxProps;
}

const StyledTableContainer = styled(TableContainer)<StyledTableContainerProps>(({ tableContainerSx, theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  minWidth: 'min-content',
  width: '100%',
  ...(unstableStyleFunctionSx({ sx: tableContainerSx, theme } as never) as Record<string, unknown>),
}));

const StyledTable = styled(Table)<{ tablesx?: SxProps }>(({ tablesx }) => {
  return { ...(unstableStyleFunctionSx({ sx: tablesx } as never) as Record<string, unknown>) };
});

const INITIAL_DISPLAY = 25;

export const BasicTableCard: FC<Props> = ({
  actionComponent,
  hasShowAll,
  headComponent,
  hintComponent,
  listComponent,
  noPadding,
  noTitle,
  tableContainerSx,
  tablesx,
  titleComponent,
  ...props
}): JSX.Element => {
  const { t } = useTranslation(['common']);
  const [showAll, setShowAll] = useState(false);

  return (
    <Paper data-testid={props['data-testid']} elevation={noPadding ? 0 : 1}>
      <Stack spacing={1} justifyContent="space-between" px={noPadding ? undefined : 3} pt={noPadding ? undefined : 3} pb={noPadding ? undefined : listComponent.length > 0 ? 1 : 3}>
        {!noTitle && <Stack direction="row" justifyContent="space-between" alignItems="center">
          {titleComponent}
          {actionComponent}
        </Stack>}
        <ResponsiveText
          variant="body2"
          color="text.secondary"
          data-testid="basic-table-hint"
          responsiveOptions={{
            minFontSize: '0.75rem',
            maxFontSize: '0.875rem',
            textLengthThreshold: 30,
          }}
        >
          {hintComponent}
        </ResponsiveText>
      </Stack>

      {listComponent.length > 0
        && <Stack spacing={1} padding={0} style={{ overflowX: 'auto' }}>
          <StyledTableContainer tableContainerSx={tableContainerSx}>
            <StyledTable component="div" size="small" tablesx={tablesx} >
              <TableHead component="div">
                <TableRow component="div">
                  {headComponent}
                </TableRow>
              </TableHead>
              <TableBody component="div">
                {listComponent.slice(0, !hasShowAll || showAll ? listComponent.length : INITIAL_DISPLAY)}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>

          {hasShowAll && listComponent.length > INITIAL_DISPLAY && !showAll
            && <Button variant="text" onClick={() => setShowAll(true)} data-testid="action-button-events-show-all">
              {t('common:common.action.show-all')}
            </Button>
          }
        </Stack>
      }
    </Paper>
  );
};
